import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Radio, RadioGroup, Switch, TextField,
} from '@mui/material'
import swal from 'sweetalert'
import { toast } from 'react-toastify'
import {
  asignarClasificacion, desactivarClasificacion, getClasificacion,
  getClasificacionSocios, getClientes, getListas,
  getPerfiles, getZonas, guardarClasificacion, guardarCliente, retirarClasificacionCliente,
} from 'src/services/preciosService'
import TablaPrecios from './TablaPrecios'
import MoneyField from './MoneyField'
import { fmt, pct, chip as chipBase } from './columnasPrecios'

/* ===================================================================================================
 * CLASIFICACIÓN DE CLIENTES — el único lugar donde se configura una condición de precio.
 *
 * En los dos Excel el socio NO aparece nunca: aparece su CLASIFICACIÓN. SAP no le pone precio al
 * cliente, le pone precio a la combinación de atributos que el cliente carga en su maestro. Esta
 * pantalla es esa combinación, con todo lo que le cuelga:
 *   1. cómo la encuentra SAP  (tabla + condición + las dimensiones de la llave)
 *   2. cómo se calcula        (fórmula + conceptos, con un ejemplo en plata)
 *   3. sus socios             (quién la tiene asignada y con qué margen negociado)
 *
 * Antes esto vivía en tres tabs separados (Tipos de lista / Márgenes / Clientes) y encima había dos
 * caminos que se contradecían para saber qué condición le pega a un socio. Ver precios_f34.sql.
 * ================================================================================================= */

/** chip del módulo + `title` para poder explicar el badge al pasar el mouse. */
const chip = (txt, bg, color, title) =>
  (title
    ? <span title={title} style={{ display: 'inline-block' }}>{chipBase(txt, bg, color)}</span>
    : chipBase(txt, bg, color))

const badgeCondicion = (c) => {
  const map = {
    ZPAU: { bg: '#eaf2fb', color: '#1c4a80', txt: 'ZPAU · costo' },
    ZPPU: { bg: '#e6f6ec', color: '#166534', txt: 'ZPPU · precio público' },
    ZDPC: { bg: '#fff4dd', color: '#7a5300', txt: 'ZDPC · % descuento' },
  }
  const s = map[c] || { bg: '#eef0f2', color: '#6b7684', txt: c }
  return chip(s.txt, s.bg, s.color)
}

// Las fórmulas se nombran con palabras. "K" quedó como alias entre paréntesis porque es lo que dice
// la plantilla de SAP y quien carga lo busca así, pero no puede ser lo ÚNICO que se lee.
const FORMULAS = [
  { v: 'margen', t: 'Margen sobre la base — sin impuestos × (1 − margen)' },
  { v: 'margen_flete', t: 'Margen con auxilio de flete — (base − flete) × (1 − margen) + fondo' },
  { v: 'base', t: 'Base sin margen — precio sin impuestos' },
  { v: 'pvp', t: 'El PVP tal cual' },
  { v: 'porcentaje', t: 'Solo un porcentaje (no carga precio)' },
  { v: 'fijo', t: 'Un valor fijo' },
]

const DIMENSIONES = [
  { v: 'tipo_lista', t: 'Tipo de lista', ayuda: 'Lo más común: el margen negociado. Ej: 11 Agente, 53 Cencosud.' },
  { v: 'grupo_precio', t: 'Grupo de precio', ayuda: 'Ej: 10 Cliente Especial, paga la base sin margen.' },
  { v: 'grupo_descuento', t: 'Grupo de descuento', ayuda: 'Almacenes a crédito: carga un %, no un precio. Ej: 018 Flamingo.' },
  { v: '', t: 'Ninguna (solo el material)', ayuda: 'Le pega a todos: es el piso. Ej: 809, ZPPU.' },
]

const mono = {
  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
  background: '#f4f6f8',
  border: '1px solid #e3e7ec',
  borderRadius: 4,
  padding: '2px 6px',
  color: '#0f2439',
  fontSize: 12,
}

const VACIO = {
  id: 0, clave: '', label: '', tabla_sap: '', condicion: 'ZPAU', transaccion: 'VK11',
  organizacion: '', dimension: 'tipo_lista', dim_valor: '', usa_clase: false, clase_documento: '',
  usa_destinatario: false, destinatario: '', espacio_material: false, formula: 'margen',
  valor_fijo: '', aplica_fondo: false, aplica_chaleco: false,
  aplica_flete: false, operacion: 'venta', precedencia: 10, orden: 500, cilindraje_max: '', nota: '',
}

/** Mismo orden que arma el SP. Verificado: reproduce las 16 llaves reales de la plantilla. */
const armarLlave = (f) => {
  let k = '{org}'
  if (f.usa_clase && f.clase_documento) k += '{clase_documento}'
  if (f.dimension && f.dim_valor) k += `{${f.dimension}}`
  if (f.usa_destinatario && f.destinatario) k += '{destinatario}'
  return `${k}${f.espacio_material ? ' ' : ''}{material}`
}

/** La llave con valores de verdad, como queda en la columna B del archivo de carga. */
const llaveConValores = (f, material = '60003638') =>
  armarLlave(f)
    .replace('{org}', f.organizacion || '1000')
    .replace('{clase_documento}', f.clase_documento || '')
    .replace(`{${f.dimension}}`, f.dim_valor || '')
    .replace('{destinatario}', f.destinatario || '')
    .replace('{material}', material)

/** Fila de la DB -> estado del formulario (round-trip). */
const aForm = (r) => {
  const dim = r.tipo_lista ? 'tipo_lista'
    : r.grupo_precio ? 'grupo_precio'
      : r.grupo_descuento ? 'grupo_descuento' : ''
  return {
    id: Number(r.id),
    clave: r.clave || '',
    label: r.label || '',
    tabla_sap: r.tabla_sap || '',
    condicion: r.condicion || 'ZPAU',
    transaccion: r.transaccion || '',
    organizacion: r.organizacion || '',
    dimension: dim,
    dim_valor: r.tipo_lista || r.grupo_precio || r.grupo_descuento || '',
    usa_clase: Boolean(r.clase_documento),
    clase_documento: r.clase_documento || '',
    usa_destinatario: Boolean(r.destinatario),
    destinatario: r.destinatario || '',
    espacio_material: Number(r.espacio_material) === 1,
    formula: r.formula || 'margen',
    valor_fijo: Number(r.valor_fijo) ? String(r.valor_fijo) : '',
    aplica_fondo: Number(r.aplica_fondo) === 1,
    aplica_chaleco: Number(r.aplica_chaleco) === 1,
    aplica_flete: Number(r.aplica_flete) === 1,
    operacion: r.operacion || 'venta',
    precedencia: Number(r.precedencia) || 10,
    orden: Number(r.orden) || 500,
    cilindraje_max: Number(r.cilindraje_max) ? String(r.cilindraje_max) : '',
    nota: r.nota || '',
  }
}

/**
 * La clave interna, propuesta sola.
 *
 * El backend la exige y tiene que ser única, pero nadie la lee: es un identificador. Pedirla a mano
 * era hacer inventar un dato al que la usa, y encima con reglas (sin espacios, mayúsculas). Se arma
 * con lo que ya se tecleó — nombre, tabla y el valor de la dimensión — y sigue siendo editable en
 * los ajustes avanzados para los casos en que haga falta una puntual.
 */
const claveSugerida = (f) => {
  const base = String(f.label || f.condicion || 'LISTA')
    // NFD parte la tilde en letra + marca; la marca se borra para que «Ó» quede «O» y no un guion.
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')
    .slice(0, 24)
  return [base, f.tabla_sap, f.dim_valor]
    .map((x) => String(x || '').toUpperCase().replace(/[^A-Z0-9]+/g, ''))
    .filter(Boolean)
    .join('_')
}

/** Estado del formulario -> payload del servlet (la llave NO se manda: la arma el backend). */
const aPayload = (f) => ({
  id: f.id || 0,
  clave: (f.clave.trim() || claveSugerida(f)).toUpperCase(),
  label: f.label.trim(),
  tabla_sap: f.tabla_sap.trim(),
  condicion: f.condicion.trim().toUpperCase(),
  transaccion: f.transaccion.trim(),
  organizacion: f.organizacion.trim(),
  tipo_lista: f.dimension === 'tipo_lista' ? f.dim_valor.trim() : '',
  grupo_precio: f.dimension === 'grupo_precio' ? f.dim_valor.trim() : '',
  grupo_descuento: f.dimension === 'grupo_descuento' ? f.dim_valor.trim() : '',
  clase_documento: f.usa_clase ? f.clase_documento.trim() : '',
  destinatario: f.usa_destinatario ? f.destinatario.trim() : '',
  espacio_material: f.espacio_material ? 1 : 0,
  formula: f.formula,
  valor_fijo: f.valor_fijo || 0,
  hereda_margen_de: 0, // f80: no hay herencia de margen entre clasificaciones
  aplica_fondo: f.aplica_fondo ? 1 : 0,
  aplica_chaleco: f.aplica_chaleco ? 1 : 0,
  aplica_flete: f.aplica_flete ? 1 : 0,
  operacion: f.operacion,
  precedencia: f.precedencia || 10,
  orden: f.orden || 500,
  cilindraje_max: f.cilindraje_max || 0,
  nota: f.nota,
})

const Titulo = ({ n, children, hint }) => (
  <div style={{ margin: '18px 0 8px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        style={{
          background: '#0f2439', color: '#fff', width: 20, height: 20, borderRadius: '50%',
          fontSize: 11.5, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {n}
      </span>
      <span style={{ fontSize: 13, fontWeight: 800, color: '#0f2439', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {children}
      </span>
    </div>
    {hint && <div style={{ fontSize: 12, color: '#6b7684', marginTop: 4, marginLeft: 28 }}>{hint}</div>}
  </div>
)

/* --------------------------------------------------------------------------------------------------
 * Formulario de la clasificación: secciones 1 y 2 (la llave SAP y la fórmula).
 * La llave se ARMA con los controles; nunca se teclea, porque un espacio de más rompe la carga a SAP.
 * ------------------------------------------------------------------------------------------------ */
const ClasificacionForm = ({ form, setForm, detalle }) => {
  // Un solo interruptor para los dos bloques de ajustes finos (la llave y el cálculo).
  const [verAvanzado, setVerAvanzado] = useState(false)
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value })
  const setChk = (k) => (e) => setForm({ ...form, [k]: e.target.checked })
  const dimAyuda = (DIMENSIONES.find((d) => d.v === form.dimension) || {}).ayuda
  const llave = llaveConValores(form)
  const tieneEspacioRaro = /\s\s/.test(llave) || /^\s|\s$/.test(llave)

  return (
    <div>
      <Titulo n={1} hint="Los atributos del socio en su maestro SAP deciden qué condición le aplica.">
        Cómo la encuentra SAP
      </Titulo>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <TextField size="small" label="Tabla SAP" value={form.tabla_sap} onChange={set('tabla_sap')} sx={{ width: 110 }} helperText="807, 808, 816…" />
        <TextField size="small" select label="Condición" value={form.condicion} onChange={set('condicion')} sx={{ width: 170 }}>
          <MenuItem value="ZPAU">ZPAU · costo</MenuItem>
          <MenuItem value="ZPPU">ZPPU · precio público</MenuItem>
          <MenuItem value="ZDPC">ZDPC · % descuento</MenuItem>
        </TextField>
        <TextField size="small" label="Nombre que ve la gente" value={form.label} onChange={set('label')} sx={{ width: 280 }} helperText="Ej: Agente 11" />
      </div>

      <div style={{ background: '#f8fafc', border: '1px solid #e3e7ec', borderRadius: 6, padding: '10px 12px', marginBottom: 10 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0f2439', marginBottom: 4 }}>
          ¿Por qué atributo del socio se busca?
        </div>
        <RadioGroup row value={form.dimension} onChange={(e) => setForm({ ...form, dimension: e.target.value, dim_valor: '' })}>
          {DIMENSIONES.map((d) => (
            <FormControlLabel key={d.v || 'nada'} value={d.v} control={<Radio size="small" />} label={<span style={{ fontSize: 12.5 }}>{d.t}</span>} />
          ))}
        </RadioGroup>
        {dimAyuda && <div style={{ fontSize: 11.5, color: '#6b7684', marginBottom: 8 }}>{dimAyuda}</div>}
        {form.dimension && (
          <TextField size="small" label="Valor" value={form.dim_valor} onChange={set('dim_valor')} sx={{ width: 130 }} helperText="Sin espacios" />
        )}

        <div style={{ marginTop: 10, fontSize: 12.5 }}>
          <span style={{ color: '#6b7684' }}>Así queda la llave: </span>
          <span style={mono}>{llave}</span>
          {tieneEspacioRaro && (
            <div style={{ color: '#b42318', fontWeight: 700, marginTop: 4 }}>
              ⚠ La llave quedó con un espacio raro. Eso rompe la carga a SAP: revisa el separador y los valores.
            </div>
          )}
          <div style={{ color: '#9aa5b1', marginTop: 2 }}>
            La <b>organización</b> y el <b>material</b> los pone cada vehículo al armar el plano.
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------------------------------
          AJUSTES AVANZADOS.

          El formulario pedía 20 campos para dar de alta una clasificación, y de esos el backend solo
          exige cuatro: clave, nombre, tabla y condición — el resto tiene default. Los que casi nunca
          se tocan quedan acá abajo, cerrados. Lo que se ve arriba es lo que de verdad distingue una
          clasificación de otra: en qué tabla vive, con qué condición, y por qué atributo la encuentra
          SAP. La clave interna dejó de pedirse: se propone sola (ver `claveSugerida`).
          ---------------------------------------------------------------------------------------- */}
      <Button
        size="small"
        onClick={() => setVerAvanzado((v) => !v)}
        sx={{ mb: 1, textTransform: 'none', color: '#1c4a80' }}
      >
        {verAvanzado ? '− Ocultar ajustes avanzados' : '+ Ajustes avanzados (casi nunca hacen falta)'}
      </Button>

      {verAvanzado && (
      <div style={{ background: '#fbfcfd', border: '1px dashed #d8dee6', borderRadius: 6, padding: '10px 12px', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <TextField
            size="small" label="Clave interna" value={form.clave} onChange={set('clave')} sx={{ width: 230 }}
            placeholder={claveSugerida(form)}
            helperText={form.clave ? 'Sin espacios' : `Vacío = se guarda como ${claveSugerida(form) || '—'}`}
          />
          <TextField size="small" label="Transacción" value={form.transaccion} onChange={set('transaccion')} sx={{ width: 120 }} />
          <TextField
            size="small" select label="Organización" value={form.organizacion} onChange={set('organizacion')}
            sx={{ width: 220 }} helperText="Vacío = la del vehículo (lo normal)"
          >
            <MenuItem value="">La que traiga el vehículo</MenuItem>
            <MenuItem value="1000">Forzar 1000</MenuItem>
            <MenuItem value="1100">Forzar 1100</MenuItem>
          </TextField>
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <FormControlLabel
              control={<Checkbox size="small" checked={form.usa_clase} onChange={setChk('usa_clase')} />}
              label={<span style={{ fontSize: 12.5 }}>Solo en cierta operación (clase de documento)</span>}
            />
            {form.usa_clase && (
              <TextField size="small" label="Clase" value={form.clase_documento} onChange={set('clase_documento')} sx={{ width: 120, display: 'block' }} helperText="ZPKE consignación, ZPOR" />
            )}
          </div>
          <div>
            <FormControlLabel
              control={<Checkbox size="small" checked={form.usa_destinatario} onChange={setChk('usa_destinatario')} />}
              label={<span style={{ fontSize: 12.5 }}>Precio exclusivo de UN cliente puntual</span>}
            />
            {form.usa_destinatario && (
              <TextField size="small" label="Código SAP del cliente" value={form.destinatario} onChange={set('destinatario')} sx={{ width: 190, display: 'block' }} helperText="Ej 550001263 (Leticia)" />
            )}
          </div>
        </div>

        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Switch size="small" checked={form.espacio_material} onChange={setChk('espacio_material')} />}
          label={
            <span style={{ fontSize: 12.5 }}>
              La llave lleva un <b>espacio</b> antes del material (sí: 808, 879, 802 · no: 807, 887, 840, 809, 816)
            </span>
          }
        />

      </div>
      )}

      <Titulo n={2} hint="Parte del PVP y el %IVA; aplica el margen y los conceptos marcados.">
        Cómo se calcula
      </Titulo>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <TextField size="small" select label="Fórmula" value={form.formula} onChange={set('formula')} sx={{ width: 420 }}>
          {FORMULAS.map((f) => <MenuItem key={f.v} value={f.v}>{f.t}</MenuItem>)}
        </TextField>
        {form.formula === 'fijo' && (
          <MoneyField label="Valor fijo" value={form.valor_fijo} onChange={(v) => setForm({ ...form, valor_fijo: v })} sx={{ width: 180 }} />
        )}
        {/* La operación va con el resto de los ajustes finos: por defecto «venta» sirve para casi
            todas. Ya no hay «Hereda el margen de» (f80): el margen se carga por moto en cada
            clasificación, desde Márgenes o desde las tarjetas al armar el plano. */}
        {verAvanzado && (
          <TextField size="small" select label="Operación" value={form.operacion} onChange={set('operacion')} sx={{ width: 165 }}>
            <MenuItem value="venta">Venta</MenuItem>
            <MenuItem value="consignacion">Consignación</MenuItem>
            <MenuItem value="descuento">Descuento</MenuItem>
            <MenuItem value="especial">Especial</MenuItem>
          </TextField>
        )}
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12.5, color: '#6b7684' }}>Conceptos que entran:</span>
        <FormControlLabel
          control={<Checkbox size="small" checked={form.aplica_flete} onChange={setChk('aplica_flete')} />}
          label={<span style={{ fontSize: 12.5 }}>Auxilio de flete {detalle ? `(${fmt(detalle.valor_flete)})` : ''} — se RESTA antes del margen</span>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={form.aplica_fondo} onChange={setChk('aplica_fondo')} />}
          label={<span style={{ fontSize: 12.5 }}>Fondo de movilidad {detalle ? `(${fmt(detalle.valor_fondo)})` : ''} — SUMA al facturado</span>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={form.aplica_chaleco} onChange={setChk('aplica_chaleco')} />}
          label={<span style={{ fontSize: 12.5 }}>Chaleco {detalle ? `(${fmt(detalle.valor_chaleco)})` : ''}</span>}
        />
      </div>

      {/* Precedencia, orden y cilindraje tienen default que sirve (10 / 500 / sin tope). Solo se
          tocan cuando una clasificación tiene que pisar a otra o llegar última a SAP. */}
      {verAvanzado && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <TextField
            size="small" type="number" label="Precedencia" value={form.precedencia} onChange={set('precedencia')}
            sx={{ width: 150 }} helperText="Mayor gana. 20 = pisa la lista"
          />
          <TextField
            size="small" type="number" label="Orden de carga" value={form.orden} onChange={set('orden')}
            sx={{ width: 150 }} helperText="La ZPPU va última"
          />
          <TextField
            size="small" type="number" label="Cilindraje máximo" value={form.cilindraje_max} onChange={set('cilindraje_max')}
            sx={{ width: 170 }} helperText="Vacío = sin tope"
          />
          <TextField size="small" label="Nota" value={form.nota} onChange={set('nota')} sx={{ flex: 1, minWidth: 260 }} />
        </div>
      )}

    </div>
  )
}

/* --------------------------------------------------------------------------------------------------
 * Panel de detalle: el formulario + margen + socios.
 * ------------------------------------------------------------------------------------------------ */
const Detalle = ({ id, puedeEditar, onGuardado }) => {
  const [detalle, setDetalle] = useState(null)
  const [form, setForm] = useState(VACIO)
  const [socios, setSocios] = useState([])
  const [error, setError] = useState('')
  const [cargando, setCargando] = useState(false)
  // margen global editable
  // asignar socio existente
  const [abreSocio, setAbreSocio] = useState(false)
  const [clientes, setClientes] = useState([])
  const [socioSel, setSocioSel] = useState('')
  const [socioMargen, setSocioMargen] = useState('')
  const [socioOperacion, setSocioOperacion] = useState('venta')
  const [socioMotivo, setSocioMotivo] = useState('')
  // alta / edición de la razón social
  const [formSocio, setFormSocio] = useState(null)
  const [perfiles, setPerfiles] = useState([])
  const [zonas, setZonas] = useState(['TODAS'])

  /** Carga COMPLETA: detalle + formulario + excepciones + socios. Al abrir y después de guardar. */
  const cargar = useCallback(async () => {
    if (!id) return
    setCargando(true)
    setError('')
    // El backend aún espera PVP/IVA/moto para el ejemplo en plata; se pasan constantes fijas para
    // que lo que carga NO cambie (el simulador se retiró).
    const [d, s] = await Promise.all([
      getClasificacion(id, '5049000', '0.19', 0),
      getClasificacionSocios(id),
    ])
    if (!d || !d.data || !d.data.length) {
      setError('No se pudo traer la clasificación. Intenta de nuevo en un momento.')
      setCargando(false)
      return
    }
    const fila = d.data[0]
    setDetalle(fila)
    setForm(aForm(fila))
    setSocios((s && s.data) || [])
    setCargando(false)
  }, [id])

  useEffect(() => { cargar() }, [cargar])

  const guardar = async () => {
    const r = await guardarClasificacion(aPayload(form))
    const fila = r && r.data && r.data[0]
    if (!fila) {
      toast.error('No se pudo guardar. Intenta de nuevo en un momento.')
      return
    }
    // los guardarraíles del SP rebotan con result = 0 y un mensaje: hay que mostrarlo, no tragarlo
    if (fila.error) {
      toast.warn(fila.error)
      return
    }
    toast.success(`Guardada. La llave quedó: ${fila.llave_patron}`)
    cargar()
    onGuardado?.()
  }

  const abrirAsignar = async () => {
    setAbreSocio(true)
    setSocioSel('')
    setSocioMargen('')
    setSocioMotivo('')
    const r = await getClientes('')
    // el select de clientes: uno por razón social (el listado viene por cliente x condición)
    const vistos = {}
    const unicos = ((r && r.data) || []).filter((c) => {
      if (!c.vendedor_id || vistos[c.vendedor_id]) return false
      vistos[c.vendedor_id] = 1
      return true
    })
    setClientes(unicos)
  }

  const asignar = async () => {
    if (!socioSel) {
      toast.warn('Elige a quién le asignas esta clasificación.')
      return
    }
    const margen = String(socioMargen).trim()
    if (margen !== '') {
      const v = Number(margen.replace(',', '.'))
      if (Number.isNaN(v) || v < 0 || v >= 100) {
        toast.warn('El margen negociado va entre 0 y 99,99 %.')
        return
      }
    }
    const r = await asignarClasificacion({
      vendedor_id: socioSel,
      lista_id: id,
      // vacío = usa el margen de la clasificación. 0 sería "gana 0%", que es otra cosa.
      margen: margen === '' ? '' : String(Number(margen.replace(',', '.')) / 100),
      operacion: socioOperacion,
      motivo: socioMotivo,
    })
    if (!r) {
      toast.error('No se pudo asignar el socio.')
      return
    }
    setAbreSocio(false)
    toast.success('Socio asignado a esta clasificación.')
    cargar()
    onGuardado?.()
  }

  /** Catálogos del formulario de socio. Se traen una sola vez, al abrirlo por primera vez. */
  const cargarCatalogos = async () => {
    if (perfiles.length || zonas.length > 1) return
    const [p, z] = await Promise.all([getPerfiles(), getZonas()])
    setPerfiles((p && p.data) || [])
    if (z && z.data) setZonas(['TODAS', ...z.data.map((r) => r.zona)])
  }

  const SOCIO_VACIO = {
    id: 0, codigo_sap: '', nombre: '', nit: '', perfil_id: '', organizacion: '1000',
    zona: 'TODAS', ciudad: '',
    // solo en el alta: quedar asignado a esta clasificación de una
    asignar: true, margen: '', operacion: 'venta', motivo: '',
  }

  const abrirSocioNuevo = async () => {
    await cargarCatalogos()
    setFormSocio({ ...SOCIO_VACIO })
  }

  const abrirSocioEdicion = async (row) => {
    await cargarCatalogos()
    setFormSocio({
      id: Number(row.vendedor_id),
      codigo_sap: row.codigo_sap || '',
      nombre: row.nombre || '',
      nit: row.nit || '',
      perfil_id: row.perfil_id ? String(row.perfil_id) : '',
      organizacion: row.organizacion || '1000',
      zona: row.zona || 'TODAS',
      ciudad: row.ciudad || '',
      asignar: false, margen: '', operacion: 'venta', motivo: '',
    })
  }

  const guardarSocio = async () => {
    const f = formSocio
    if (!f.nombre.trim()) {
      toast.warn('El nombre de la razón social es obligatorio.')
      return
    }
    if (f.asignar && f.margen.trim() !== '') {
      const v = Number(f.margen.replace(',', '.'))
      if (Number.isNaN(v) || v < 0 || v >= 100) {
        toast.warn('El margen negociado va entre 0 y 99,99 %.')
        return
      }
    }
    const r = await guardarCliente({
      id: f.id || 0,
      codigo_sap: f.codigo_sap.trim(),
      nombre: f.nombre.trim(),
      nit: f.nit.trim(),
      perfil_id: f.perfil_id || 0,
      organizacion: f.organizacion,
      zona: f.zona,
      ciudad: f.ciudad.trim(),
    })
    const fila = r && r.data && r.data[0]
    if (!fila) {
      toast.error('No se pudo guardar el socio.')
      return
    }
    // el SP rebota el código SAP repetido: es la llave de reconciliación contra SAP
    if (fila.error) {
      toast.warn(fila.error)
      return
    }
    // en el alta, dejarlo ya asignado a esta clasificación (dar de alta = crear + clasificar)
    if (!f.id && f.asignar) {
      const a = await asignarClasificacion({
        vendedor_id: String(fila.result),
        lista_id: id,
        margen: f.margen.trim() === '' ? '' : String(Number(f.margen.replace(',', '.')) / 100),
        operacion: f.operacion,
        motivo: f.motivo,
      })
      if (!a) toast.warn('El socio se creó, pero no se pudo asignarlo a esta clasificación.')
      else toast.success(`${f.nombre.trim()} creado y asignado a ${detalle.label}.`)
    } else {
      toast.success(f.id ? 'Socio actualizado.' : `${f.nombre.trim()} creado.`)
    }
    setFormSocio(null)
    cargar()
    onGuardado?.()
  }

  const retirarSocio = async (row) => {
    const ok = await swal({
      title: '¿Retirarle esta clasificación?',
      text: `${row.nombre} deja de tener ${detalle.label}. No se borra nada: queda la historia con la vigencia cerrada.`,
      buttons: ['Cancelar', 'Retirar'],
    })
    if (!ok) return
    const r = await retirarClasificacionCliente(row.vendedor_lista_id)
    if (!r) {
      toast.error('No se pudo retirar.')
      return
    }
    toast.success(`${row.nombre}: clasificación retirada.`)
    cargar()
    onGuardado?.()
  }


  const colSocios = useMemo(() => [
    { accessorKey: 'nombre', header: 'Socio', size: 220 },
    {
      id: 'codigo',
      header: 'Código SAP',
      size: 130,
      accessorFn: (r) => r.codigo_sap || '',
      Cell: ({ row }) => (row.original.codigo_sap
        ? <span style={mono}>{row.original.codigo_sap}</span>
        : chip('falta', '#fdecea', '#b42318', 'Sin código SAP no se puede reconciliar con el maestro de clientes')),
    },
    { accessorKey: 'organizacion', header: 'Org.', size: 70 },
    { accessorKey: 'zona', header: 'Zona', size: 110 },
    {
      id: 'operacion',
      header: 'Operación',
      size: 120,
      accessorFn: (r) => r.operacion || '',
      Cell: ({ row }) => (row.original.operacion === 'consignacion'
        ? chip('consignación', '#f3e8ff', '#6b21a8')
        : <span style={{ fontSize: 12 }}>{row.original.operacion}</span>),
    },
    {
      id: 'margen',
      header: 'Margen que se le aplica',
      size: 200,
      accessorFn: (r) => Number(r.margen_efectivo || 0),
      Cell: ({ row }) => {
        const r = row.original
        const propio = r.margen_negociado !== null && r.margen_negociado !== undefined && r.margen_negociado !== ''
        return (
          <span style={{ fontSize: 12.5 }}>
            <b>{pct(r.margen_efectivo)}</b>{' '}
            {propio
              ? chip('negociado', '#fff4dd', '#7a5300', 'Este socio tiene un margen propio, negociado aparte')
              : <span style={{ color: '#9aa5b1' }}>sin margen negociado</span>}
          </span>
        )
      },
    },
    {
      id: 'sap',
      header: 'SAP',
      size: 115,
      accessorFn: (r) => r.sync_estado || '',
      Cell: ({ row }) => {
        const e = row.original.sync_estado
        if (e === 'ok') return chip('coincide', '#e6f6ec', '#166534')
        if (e === 'divergente') return chip('divergente', '#fdecea', '#b42318')
        return chip('sin feed', '#eef0f2', '#6b7684', 'SAP todavía no reporta este socio')
      },
    },
    { accessorKey: 'vigente_desde', header: 'Desde', size: 105 },
    { accessorKey: 'motivo', header: 'Motivo de la negociación', size: 250 },
    ...(puedeEditar ? [{
      id: 'acc',
      header: '',
      size: 160,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <span style={{ display: 'flex', gap: 4 }}>
          <Button size="small" onClick={() => abrirSocioEdicion(row.original)}>Editar</Button>
          <Button size="small" color="warning" onClick={() => retirarSocio(row.original)}>Retirar</Button>
        </span>
      ),
    }] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [puedeEditar, detalle])

  if (error) {
    return (
      <div className="precios-card">
        <div style={{ color: '#b42318', fontWeight: 700, marginBottom: 8 }}>{error}</div>
        <Button size="small" variant="outlined" onClick={cargar}>Reintentar</Button>
      </div>
    )
  }
  if (!detalle) return <div className="precios-card" style={{ color: '#6b7684' }}>Cargando…</div>

  return (
    <div className="precios-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#0f2439' }}>{detalle.label}</span>
        {badgeCondicion(detalle.condicion)}
        <span style={{ fontSize: 12, color: '#6b7684' }}>tabla {detalle.tabla_sap}</span>
        {Number(detalle.socios_n) > 0 && chip(`${detalle.socios_n} socio(s)`, '#eaf2fb', '#1c4a80')}
        {Number(detalle.precedencia) > 10 && chip('gana sobre la lista', '#fff4dd', '#7a5300')}
        {Number(detalle.lineas_publicadas) > 0
          && chip(`${detalle.lineas_publicadas} línea(s) ya cargadas`, '#eef0f2', '#6b7684',
            'Líneas de plano publicadas con esta condición: si la desactivas, dejan de generarse nuevas')}
        {Number(detalle.activo) === 0 && chip('desactivada', '#fdecea', '#b42318')}
      </div>

      <ClasificacionForm
        form={form}
        setForm={setForm}
        detalle={detalle}
      />

      {puedeEditar && (
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <Button variant="contained" size="small" onClick={guardar} disabled={cargando}>
            Guardar la clasificación
          </Button>
          <Button variant="outlined" size="small" onClick={cargar} disabled={cargando}>Descartar cambios</Button>
        </div>
      )}

      <Titulo
        n={3}
        hint="Ojo: esta asignación NO se empuja a SAP. El tipo de lista se pone en el maestro del cliente."
      >
        Socios que la tienen asignada
      </Titulo>

      {puedeEditar && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <Button size="small" variant="contained" onClick={abrirSocioNuevo}>
            Crear socio nuevo
          </Button>
          <Button size="small" variant="outlined" onClick={abrirAsignar}>
            Asignar un socio que ya existe
          </Button>
        </div>
      )}
      <TablaPrecios columns={colSocios} data={socios} isLoading={cargando} pageSize={10} />

      <Dialog open={abreSocio} onClose={() => setAbreSocio(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontSize: 16 }}>Asignar un socio a «{detalle.label}»</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 12.5, color: '#6b7684', marginBottom: 12 }}>
            Queda asignado desde hoy. Si le pones un margen, le gana al del motor.
          </div>
          <TextField
            size="small" select fullWidth label="Socio" value={socioSel}
            onChange={(e) => setSocioSel(e.target.value)} sx={{ mb: 2 }}
          >
            {clientes.map((c) => (
              <MenuItem key={c.vendedor_id} value={String(c.vendedor_id)}>
                {c.nombre}{c.codigo_sap ? ` · ${c.codigo_sap}` : ' · sin código SAP'}
              </MenuItem>
            ))}
          </TextField>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <TextField
              size="small" label="Margen negociado" value={socioMargen} onChange={(e) => setSocioMargen(e.target.value)}
              sx={{ width: 180 }} InputProps={{ endAdornment: <span style={{ color: '#6b7684' }}>%</span> }}
              helperText="Vacío = el margen que resuelva el motor para cada moto"
            />
            <TextField
              size="small" select label="Operación" value={socioOperacion}
              onChange={(e) => setSocioOperacion(e.target.value)} sx={{ width: 175 }}
            >
              <MenuItem value="venta">Venta</MenuItem>
              <MenuItem value="consignacion">Consignación</MenuItem>
              <MenuItem value="descuento">Descuento</MenuItem>
            </TextField>
          </div>
          <TextField
            size="small" fullWidth label="Motivo (queda auditado)" value={socioMotivo}
            onChange={(e) => setSocioMotivo(e.target.value)}
            placeholder="Ej: negociación Cencosud jul-2026, margen 7% → lista 53"
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setAbreSocio(false)}>Cancelar</Button>
          <Button size="small" variant="contained" onClick={asignar}>Asignar</Button>
        </DialogActions>
      </Dialog>

      {/* Alta / edición de la razón social. Dar de alta un socio = crearlo + clasificarlo. */}
      <Dialog open={Boolean(formSocio)} onClose={() => setFormSocio(null)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ fontSize: 16 }}>
          {formSocio && formSocio.id ? `Editar «${formSocio.nombre}»` : 'Crear un socio nuevo'}
        </DialogTitle>
        <DialogContent>
          {formSocio && (
            <>
              <div style={{ fontSize: 12.5, color: '#6b7684', marginBottom: 12 }}>
                Lo crea <b>en esta app</b>. En el <b>maestro de clientes de SAP</b> se crea aparte.
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <TextField
                  size="small" label="Razón social" value={formSocio.nombre}
                  onChange={(e) => setFormSocio({ ...formSocio, nombre: e.target.value })}
                  sx={{ width: 300 }} required
                />
                <TextField
                  size="small" label="Código SAP" value={formSocio.codigo_sap}
                  onChange={(e) => setFormSocio({ ...formSocio, codigo_sap: e.target.value })}
                  sx={{ width: 175 }}
                  helperText="Con esto se reconcilia contra SAP"
                />
                <TextField
                  size="small" label="NIT" value={formSocio.nit}
                  onChange={(e) => setFormSocio({ ...formSocio, nit: e.target.value })}
                  sx={{ width: 165 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <TextField
                  size="small" select label="Organización" value={formSocio.organizacion}
                  onChange={(e) => setFormSocio({ ...formSocio, organizacion: e.target.value })}
                  sx={{ width: 140 }}
                >
                  <MenuItem value="1000">1000</MenuItem>
                  <MenuItem value="1100">1100</MenuItem>
                </TextField>
                <TextField
                  size="small" select label="Zona" value={formSocio.zona}
                  onChange={(e) => setFormSocio({ ...formSocio, zona: e.target.value })}
                  sx={{ width: 220 }}
                >
                  {zonas.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
                </TextField>
                <TextField
                  size="small" label="Ciudad" value={formSocio.ciudad}
                  onChange={(e) => setFormSocio({ ...formSocio, ciudad: e.target.value })}
                  sx={{ width: 175 }}
                />
              </div>
              <TextField
                size="small" select label="Perfil (solo etiqueta para agrupar)"
                value={formSocio.perfil_id}
                onChange={(e) => setFormSocio({ ...formSocio, perfil_id: e.target.value })}
                sx={{ width: 320, mb: 2 }}
                helperText="Las condiciones salen de sus clasificaciones, no del perfil"
              >
                <MenuItem value="">Sin perfil</MenuItem>
                {perfiles.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>{p.label || p.clave}</MenuItem>
                ))}
              </TextField>

              {!formSocio.id && (
                <div style={{ background: '#f8fafc', border: '1px solid #e3e7ec', borderRadius: 6, padding: '10px 12px' }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small" checked={formSocio.asignar}
                        onChange={(e) => setFormSocio({ ...formSocio, asignar: e.target.checked })}
                      />
                    )}
                    label={<span style={{ fontSize: 12.5 }}>Dejarlo ya con la clasificación <b>{detalle.label}</b></span>}
                  />
                  {formSocio.asignar && (
                    <>
                      <div style={{ display: 'flex', gap: 10, marginTop: 6, marginBottom: 10 }}>
                        <TextField
                          size="small" label="Margen negociado" value={formSocio.margen}
                          onChange={(e) => setFormSocio({ ...formSocio, margen: e.target.value })}
                          sx={{ width: 180 }}
                          InputProps={{ endAdornment: <span style={{ color: '#6b7684' }}>%</span> }}
                          helperText="Vacío = el margen que resuelva el motor para cada moto"
                        />
                        <TextField
                          size="small" select label="Operación" value={formSocio.operacion}
                          onChange={(e) => setFormSocio({ ...formSocio, operacion: e.target.value })}
                          sx={{ width: 175 }}
                        >
                          <MenuItem value="venta">Venta</MenuItem>
                          <MenuItem value="consignacion">Consignación</MenuItem>
                          <MenuItem value="descuento">Descuento</MenuItem>
                        </TextField>
                      </div>
                      <TextField
                        size="small" fullWidth label="Motivo (queda auditado)" value={formSocio.motivo}
                        onChange={(e) => setFormSocio({ ...formSocio, motivo: e.target.value })}
                        placeholder="Ej: nueva razón social La Ceja, lista 11 margen 14%"
                      />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setFormSocio(null)}>Cancelar</Button>
          <Button size="small" variant="contained" onClick={guardarSocio}>
            {formSocio && formSocio.id ? 'Guardar cambios' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

/* ================================================================================================ */

const ClasificacionesTab = ({ puedeEditar = false, onChanged }) => {
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [sel, setSel] = useState(0)
  const [nueva, setNueva] = useState(null)

  const fetchRows = useCallback(async () => {
    setIsLoading(true)
    setError('')
    // Solo las que se usan hoy: las apagadas no se muestran (se quitó el «Mostrar también…»).
    const r = await getListas(false)
    if (!r || !r.data) {
      setError('No se pudieron traer las clasificaciones. Intenta de nuevo en un momento.')
      setRows([])
    } else {
      setRows(r.data)
    }
    setIsLoading(false)
  }, [])

  useEffect(() => { fetchRows() }, [fetchRows])

  const desactivar = async (row) => {
    const aviso = Number(row.socios_n) > 0 ? ` Ojo: tiene ${row.socios_n} socio(s) asignados.` : ''
    const ok = await swal({
      title: '¿Desactivar la clasificación?',
      text: `${row.label} deja de generar líneas en los planos nuevos. No se borra nada: es reversible.${aviso}`,
      buttons: ['Cancelar', 'Desactivar'],
    })
    if (!ok) return
    const r = await desactivarClasificacion(row.id)
    const fila = r && r.data && r.data[0]
    if (!fila) {
      toast.error('No se pudo desactivar.')
      return
    }
    if (fila.error) {
      toast.warn(fila.error)
      return
    }
    toast.success(`${row.label} desactivada.`)
    fetchRows()
    onChanged?.()
  }

  const crear = async () => {
    const r = await guardarClasificacion(aPayload(nueva))
    const fila = r && r.data && r.data[0]
    if (!fila) {
      toast.error('No se pudo crear. Intenta de nuevo en un momento.')
      return
    }
    if (fila.error) {
      toast.warn(fila.error)
      return
    }
    setNueva(null)
    setSel(Number(fila.result))
    toast.success(`Clasificación creada. La llave quedó: ${fila.llave_patron}`)
    fetchRows()
    onChanged?.()
  }

  const columns = useMemo(() => [
    { accessorKey: 'tabla_sap', header: 'Tabla', size: 70 },
    {
      id: 'condicion',
      header: 'Condición',
      size: 160,
      accessorFn: (r) => r.condicion || '',
      Cell: ({ row }) => badgeCondicion(row.original.condicion),
    },
    { accessorKey: 'label', header: 'Clasificación', size: 240 },
    {
      id: 'dimension',
      header: 'Se busca por',
      size: 250,
      accessorFn: (r) => [
        r.tipo_lista ? `tipo lista ${r.tipo_lista}` : '',
        r.grupo_precio ? `grupo precio ${r.grupo_precio}` : '',
        r.grupo_descuento ? `grupo descuento ${r.grupo_descuento}` : '',
        r.clase_documento ? `clase ${r.clase_documento}` : '',
        r.destinatario ? `cliente ${r.destinatario}` : '',
      ].filter(Boolean).join(' · ') || 'solo material (todos)',
      Cell: ({ row }) => {
        const r = row.original
        const partes = [
          r.tipo_lista ? `tipo lista ${r.tipo_lista}` : '',
          r.grupo_precio ? `grupo precio ${r.grupo_precio}` : '',
          r.grupo_descuento ? `grupo descuento ${r.grupo_descuento}` : '',
          r.clase_documento ? `clase ${r.clase_documento}` : '',
          r.destinatario ? `cliente ${r.destinatario}` : '',
        ].filter(Boolean).join(' · ')
        return (
          <span style={{ fontSize: 11.5 }}>
            {partes || <span style={{ color: '#9aa5b1' }}>solo material (le pega a todos)</span>}
            {r.llave_ejemplo && <div style={{ ...mono, marginTop: 2, display: 'inline-block', fontSize: 11 }}>{r.llave_ejemplo}</div>}
          </span>
        )
      },
    },
    {
      id: 'socios',
      header: 'Socios',
      size: 90,
      accessorFn: (r) => Number(r.socios_n || 0),
      Cell: ({ row }) => (Number(row.original.socios_n)
        ? <b>{row.original.socios_n}</b>
        : chip('ninguno', '#eef0f2', '#6b7684')),
    },
    {
      id: 'activo',
      header: 'Activa',
      size: 100,
      accessorFn: (r) => (Number(r.activo) === 1 ? 'si' : 'no'),
      Cell: ({ row }) => (Number(row.original.activo) === 1
        ? chip('Sí', '#e6f6ec', '#166534')
        : chip('No se usa', '#eef0f2', '#6b7684')),
    },
    {
      id: 'acc',
      header: '',
      size: 150,
      enableColumnFilter: false,
      enableGlobalFilter: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <span style={{ display: 'flex', gap: 4 }}>
          <Button size="small" onClick={() => setSel(Number(row.original.id))}>
            {Number(sel) === Number(row.original.id) ? 'Abierta' : 'Configurar'}
          </Button>
          {puedeEditar && Number(row.original.activo) === 1 && (
            <Button size="small" color="warning" onClick={() => desactivar(row.original)}>Apagar</Button>
          )}
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [sel, puedeEditar])

  return (
    <div>
      <div className="precios-card">
        <div className="precios-section-title">Reglas de carga de planos</div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          {puedeEditar && (
            <Button size="small" variant="contained" onClick={() => setNueva({ ...VACIO })}>
              Nueva clasificación
            </Button>
          )}
        </div>

        {error ? (
          <div>
            <div style={{ color: '#b42318', fontWeight: 700, marginBottom: 8 }}>{error}</div>
            <Button size="small" variant="outlined" onClick={fetchRows}>Reintentar</Button>
          </div>
        ) : (
          <TablaPrecios
            columns={columns}
            data={rows}
            isLoading={isLoading}
            pageSize={20}
            columnPinning={{ left: ['label'], right: ['acc'] }}
          />
        )}
      </div>

      <Dialog open={sel > 0} onClose={() => setSel(0)} maxWidth="lg" fullWidth>
        <DialogTitle style={{ fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          Configurar clasificación
          <Button size="small" onClick={() => setSel(0)}>Cerrar</Button>
        </DialogTitle>
        <DialogContent dividers>
          {sel > 0 && (
            <Detalle
              key={sel}
              id={sel}
              puedeEditar={puedeEditar}
              onGuardado={() => { fetchRows(); onChanged?.() }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSel(0)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(nueva)} onClose={() => setNueva(null)} maxWidth="lg" fullWidth>
        <DialogTitle style={{ fontSize: 16 }}>Nueva clasificación</DialogTitle>
        <DialogContent>
          {nueva && (
            <ClasificacionForm
              form={nueva}
              setForm={setNueva}
              detalle={null}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setNueva(null)}>Cancelar</Button>
          <Button size="small" variant="contained" onClick={crear}>Crear</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ClasificacionesTab
